import { RequestHandler, graphql, http } from 'msw';
import { fetchDemoConfig, mapDemoConfigToMswData, resolveDemoConfig, type DemoConfig } from '@/demo/demoConfig';
import { seedDb } from '@/demo/demoDb';
import { DemoSupplier } from '@/demo/demoMode';
import {
  editBenchmarkOffer,
  editOffer,
  editStaticOffer,
  getClearingInvoices,
  getClearingInvoiceStats,
  getOffers,
  getPlayableInvoices,
  getPlayableInvoicesCsv,
  getPlayableInvoiceStats,
  getRecentClears,
  getSupplierMarketClears,
  getSupplierMarketClearsStats,
  getSupplierMarkets,
  manageExclusions,
  submitBenchmarkOffer,
  submitExpressAcceptOffer,
  submitOffer,
  submitStaticOffer,
  toggleOffer,
} from '@/demo/demoSerialize';
import { Features } from '@/enterprise/features';
import {
  BenchmarkPatchBody,
  BenchmarkPostBody,
  ClearingInvoiceStatsRequestBody,
  ClearingInvoiceStatsResponse,
  ClearingInvoicesRequestBody,
  ClearingInvoicesResponse,
  ExpressAcceptPostBody,
  ManageExclusionsBody,
  ManageExclusionsResponse,
  PatchBody,
  PlayableInvoiceStatsRequestBody,
  PlayableInvoicesRequestBody,
  PostBody,
  StaticPatchBody,
  StaticPostBody,
  type MswData,
  makeAdjustmentInvoiceStatsHandler,
  makeAdjustmentInvoicesHandler,
  makeArtifactsHandler,
  makeBuyerCashflowHandler,
  makeBuyersExpansionHandler,
  makeBuyersExpansionTrendingHandler,
  makeBuyersExpansionTrendingVoteHandler,
  makeBuyersHandler,
  makeBuyersLendingHandler,
  makeBenchmarkToggleExcludeNewInvoicesHandler,
  makeBuyersLinkingRequestsHandler,
  makeCreateBuyerExpansionRequestHandler,
  makeCreateBuyerLinkingRequestHandler,
  makeExpressAcceptPostHandler,
  makeCreateBuyersLendingHandler,
  makeDeleteOfferHandler,
  makePayDateAnnouncementsHandler,
  makePreferredAutoRenewalHandler,
  makePreferredTermRequestRenewalHandler,
  makePriceDiscoveryToggleExcludeNewInvoicesHandler,
  makeStaticToggleExcludeNewInvoicesHandler,
  makeClearSchedulesHandler,
  makeClearingInvoicesHandler,
  makeClearingInvoiceStatsHandler,
  makeConfirmSupplierAgreementsHandler,
  makeCreditNotesHandler,
  makeDismissHandler,
  makeDismissiblesHandler,
  makeEligibleInvoicesCsvHandler,
  makeExchangeRatesHandler,
  makeFeatureConfigsHandler,
  makeForgetMeHandler,
  makeForgotPasswordHandler,
  makeGetCustomAgreementWithContentHandler,
  makeGetCustomAgreementsHandler,
  makeGetMeHandler,
  makeInformationRequestInvoiceRulesHandler,
  makeInformationRequestPreferredTermHandler,
  makeInvoiceRulesHandler,
  makeLockedInvoiceStatsHandler,
  makeLockedInvoicesHandler,
  makeLegacyInvoiceHandlers,
  makeManageExclusionsHandler,
  makeOfferStatsStatusHandler,
  makeOffersHandler,
  makePlayableInvoiceStatsHandler,
  makePlayableInvoicesCsvHandler,
  makeLockedInvoicesCsvHandler,
  makeAdjustmentInvoicesCsvHandler,
  makeSupplierMarketClearCsvHandler,
  makeInstantPayStatsHandler,
  makeInstantPayStatementCsvHandler,
  makePlayableInvoicesHandler,
  makeBenchmarkPatchHandler,
  makeBenchmarkPostHandler,
  makePriceDiscoveryPatchHandler,
  makePriceDiscoveryPostHandler,
  makePricingSuggestionsHandler,
  makePushEventsHandler,
  makeRecentClearsHandler,
  makeStaticPatchHandler,
  makeStaticPostHandler,
  makeSupplierAgreementsHandler,
  makeSupplierMarketClearsHandler,
  makeSupplierMarketClearsStatsHandler,
  makeSupplierMarketsHandler,
  makeToggleOfferStatusHandler,
  makeUpdateCompanyProfileHandler,
  makeUpdateLocationHandler,
  makeUpdateUserCurrencyHandler,
  makeUpdateUserLanguageHandler,
  makeUpdateUserPreferencesHandler,
  makeUpdateUserProfileHandler,
  makeUserLendingOpportunitiesHandler,
  makeUserPreferencesHandler,
  makeUserSuppliersHandler,
  makeUserTermsHandler,
} from '@/test/mocks/sharedHandlers';

/**
 * Demo mode reuses the shared MSW handler factories (src/test/mocks/sharedHandlers) so there is a
 * single source of truth for mocked responses. The only demo-specific concern is resolving
 * the data: a demo config is fetched by UUID (or the bundled default) and mapped into the
 * MswData payload the shared handlers serve.
 *
 * Live-state routes (supplier-markets, offers, offer-stats-status, price-discovery writes) are
 * wired to demoSerialize getters/mutators so dashboard numbers update after offer submit/edit.
 * Static routes (identity, exchange rates, feature configs, etc.) still serve from MswData.
 *
 * To cover a new route in demo, add a slice + factory to the shared handler layer rather
 * than re-defining a handler here.
 */

// Split treatment overrides derived from the active demo config. Built once during demo handler
// building (loadDemoHandlers) from the same resolved config that seeds MSW, and read synchronously
// by the features layer (getFeatureTreatment). Stays empty outside an active demo session so
// production Split treatments are unaffected.
let demoFeatureTreatments: SplitIO.MockedFeaturesMap = {};
let activeDemoName: string | null = null;

/** Returns the name of the active demo config, or null when running on the bundled default. */
export function getActiveDemoName(): string | null {
  return activeDemoName;
}

/**
 * Maps demo-config product toggles to Split treatments. Only the flags listed here are forced from
 * the demo config; every other flag keeps its real Split treatment. Returns an empty map when there
 * is no active demo config.
 */
export function buildDemoFeatureTreatments(config: DemoConfig | null): SplitIO.MockedFeaturesMap {
  if (!config) {
    return {};
  }

  const overrides: SplitIO.MockedFeaturesMap = {};

  const financeIQ = config.config?.products?.financeIQ;
  if (financeIQ !== undefined) {
    overrides[Features['enterprise-ui_enableFinanceIQ']] = financeIQ ? 'on' : 'off';
  }

  const lendingConnections = config.config?.products?.lendingConnections;
  if (lendingConnections !== undefined) {
    const treatment = lendingConnections ? 'on' : 'off';
    overrides[Features['enterprise-ui_enableLendingWidget']] = treatment;
    overrides[Features['enterprise-ui_enableLendingConnectionsIntegration']] = treatment;
  }

  // Force the artifact path on so the injected demo marketing artifact takes priority over the
  // client-flag fallback in useClientMarketingWidget — which depends on real Split treatments.
  overrides[Features['enterprise-ui_useMarketingArtifacts']] = 'on';

  return overrides;
}

/** Synchronously returns the cached demo treatment overrides (empty outside demo mode). */
export function getDemoFeatureTreatments(): SplitIO.MockedFeaturesMap {
  return demoFeatureTreatments;
}

/** Derives the JWT-shaping DemoSupplier from the resolved supplier (single source of truth). */
function toDemoSupplier(supplier: MswData['supplier']): DemoSupplier {
  return {
    firstName: supplier.personalInfo?.firstName ?? 'Demo',
    lastName: supplier.personalInfo?.lastName ?? 'User',
    email: supplier.emailAddress ?? 'demo@c2fo.com',
    authServiceUuid: supplier.account?.id ?? '00000000-0000-0000-0000-000000000001',
  };
}

/**
 * Demo-mode handler set — the live-state read/write routes are state-backed variants that read
 * from / mutate the @msw/data collections in demoDb.ts via the demoSerialize getters and
 * mutators; the remaining static routes serve from MswData.
 */
function buildDemoHandlers(
  data: MswData,
  demoSerialize: {
    getSupplierMarkets: () => MswData['supplierMarkets'];
    getOffers: () => MswData['offers'];
    getPlayableInvoices: (body: PlayableInvoicesRequestBody) => MswData['playableInvoices'];
    getPlayableInvoiceStats: (body: PlayableInvoiceStatsRequestBody) => MswData['playableInvoiceStats'];
    getPlayableInvoicesCsv: (body: PlayableInvoicesRequestBody) => string;
    getClearingInvoices: (body: ClearingInvoicesRequestBody) => ClearingInvoicesResponse;
    getClearingInvoiceStats: (body: ClearingInvoiceStatsRequestBody) => ClearingInvoiceStatsResponse;
    getPricingSuggestions: () => MswData['pricingSuggestions'];
    getSupplierMarketClears: (request: Request) => MswData['supplierMarketClears'];
    getRecentClears: () => MswData['recentClears'];
    getSupplierMarketClearsStats: (request: Request) => MswData['supplierMarketClearsStats'];
    submitOffer: (body: PostBody) => Promise<string>;
    editOffer: (body: PatchBody) => Promise<void>;
    submitStaticOffer: (body: StaticPostBody) => Promise<string>;
    editStaticOffer: (body: StaticPatchBody) => Promise<void>;
    submitBenchmarkOffer: (body: BenchmarkPostBody) => Promise<string>;
    editBenchmarkOffer: (body: BenchmarkPatchBody) => Promise<void>;
    submitExpressAcceptOffer: (body: ExpressAcceptPostBody) => Promise<string>;
    toggleOffer: (uuid: string) => Promise<void>;
    manageExclusions: (body: ManageExclusionsBody) => Promise<ManageExclusionsResponse['data']>;
  }
): RequestHandler[] {
  return [
    makeGetMeHandler(data.supplier),
    makeExchangeRatesHandler(data.exchangeRates),
    makeFeatureConfigsHandler(data.featureConfigs),
    makeSupplierMarketsHandler(demoSerialize.getSupplierMarkets),
    makeOffersHandler(demoSerialize.getOffers),
    makePlayableInvoicesHandler(demoSerialize.getPlayableInvoices),
    makePlayableInvoiceStatsHandler(demoSerialize.getPlayableInvoiceStats),
    makeClearingInvoicesHandler(demoSerialize.getClearingInvoices),
    makeClearingInvoiceStatsHandler(demoSerialize.getClearingInvoiceStats),
    makeManageExclusionsHandler(demoSerialize.manageExclusions),
    makePricingSuggestionsHandler(demoSerialize.getPricingSuggestions),
    makeUserSuppliersHandler(data.userSuppliers),
    makeUserTermsHandler(data.userTerms),
    makeUserPreferencesHandler(data.userPreferences),
    makeUpdateUserPreferencesHandler(data.userPreferences),
    makeInvoiceRulesHandler(data.invoiceRules),
    makeLockedInvoicesHandler(data.lockedInvoices),
    makeLockedInvoiceStatsHandler(data.lockedInvoiceStats),
    makeToggleOfferStatusHandler(data.toggleOfferStatus, demoSerialize.toggleOffer),
    makeSupplierMarketClearsHandler(demoSerialize.getSupplierMarketClears),
    makeCreditNotesHandler(data.creditNotes),
    makeRecentClearsHandler(demoSerialize.getRecentClears),
    makeSupplierMarketClearsStatsHandler(demoSerialize.getSupplierMarketClearsStats),
    makeSupplierAgreementsHandler(data.supplierAgreements),
    makeConfirmSupplierAgreementsHandler(),
    makePriceDiscoveryPostHandler(data.priceDiscoveryPost, demoSerialize.submitOffer),
    makePriceDiscoveryPatchHandler(data.priceDiscoveryPatch, demoSerialize.editOffer),
    makeStaticPostHandler(demoSerialize.submitStaticOffer),
    makeStaticPatchHandler(demoSerialize.editStaticOffer),
    makeBenchmarkPostHandler(demoSerialize.submitBenchmarkOffer),
    makeBenchmarkPatchHandler(demoSerialize.editBenchmarkOffer),
    makeExpressAcceptPostHandler(demoSerialize.submitExpressAcceptOffer),
    makeOfferStatsStatusHandler(),
    makeBuyersExpansionHandler(data.buyersExpansion),
    makeBuyersLinkingRequestsHandler(data.buyersLinkingRequests),
    makeBuyersHandler(data.buyers),
    makeArtifactsHandler(data.artifacts),
    makeAdjustmentInvoiceStatsHandler(data.adjustmentInvoiceStats),
    makePushEventsHandler(),
    // Account settings — GraphQL mutations
    makeUpdateUserProfileHandler(),
    makeUpdateCompanyProfileHandler(),
    makeUpdateLocationHandler(),
    makeUpdateUserCurrencyHandler(),
    makeUpdateUserLanguageHandler(),
    makeGetCustomAgreementsHandler(),
    makeGetCustomAgreementWithContentHandler(),
    makeForgetMeHandler(),
    makeForgotPasswordHandler(),
    makeDismissiblesHandler(data.dismissibles),
    makeDismissHandler(),
    makeClearSchedulesHandler(data.clearSchedules),
    makeBuyersExpansionTrendingHandler(data.buyersExpansionTrending),
    makeBuyersExpansionTrendingVoteHandler(),
    makeBuyersLendingHandler(data.buyersLending),
    makeCreateBuyersLendingHandler(),
    makeUserLendingOpportunitiesHandler(data.userLendingOpportunities),
    makeAdjustmentInvoicesHandler(data.adjustmentInvoices),
    makeInformationRequestPreferredTermHandler(),
    makeInformationRequestInvoiceRulesHandler(),
    makePlayableInvoicesCsvHandler(demoSerialize.getPlayableInvoicesCsv),
    makeLockedInvoicesCsvHandler(),
    makeAdjustmentInvoicesCsvHandler(),
    makeSupplierMarketClearCsvHandler(),
    makeInstantPayStatsHandler({ data: [] }),
    makeInstantPayStatementCsvHandler(),
    makeEligibleInvoicesCsvHandler(),
    makeBuyerCashflowHandler(data.buyerCashflow),
    makePayDateAnnouncementsHandler(data.payDateAnnouncements),
    makePreferredAutoRenewalHandler(data.preferredAutoRenewal),
    makePreferredTermRequestRenewalHandler(),
    makeDeleteOfferHandler(),
    makePriceDiscoveryToggleExcludeNewInvoicesHandler(),
    makeBenchmarkToggleExcludeNewInvoicesHandler(),
    makeStaticToggleExcludeNewInvoicesHandler(),
    makeCreateBuyerLinkingRequestHandler(),
    makeCreateBuyerExpansionRequestHandler(),
    ...makeLegacyInvoiceHandlers(),
    // Catch-all safety net — must be last so every explicit factory above takes priority.
    // Session/auth-infra routes (refresh-token, magic-login/exchange, oauth/authorize) that the
    // demo never meaningfully exercises fall here and get a benign empty response instead of
    // leaking to the real UAT gateway. A console.warn makes gaps visible during development.
    http.all(`${import.meta.env.VITE_GATEWAY_URL}/*`, ({ request }) => {
      console.warn('[demo] unhandled REST route', request.method, request.url);
      return new Response(JSON.stringify({ data: [] }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }),
    graphql.operation(({ operationName }) => {
      console.warn('[demo] unhandled GraphQL operation', operationName);
      return Response.json({ data: {} });
    }),
  ];
}

/**
 * Loads MSW handlers for the given demo config UUID.
 * Fetches the config from the Admin API (not built yet — returns null today) and builds
 * all handlers from the resulting scenario, seeding the in-memory collections first.
 */
export async function loadDemoHandlers(
  demoId: string
): Promise<{ supplier: DemoSupplier; handlers: RequestHandler[] }> {
  const rawConfig = await fetchDemoConfig(demoId).catch(() => null);
  const config = resolveDemoConfig(rawConfig);
  activeDemoName = rawConfig?.name ?? null;
  demoFeatureTreatments = buildDemoFeatureTreatments(config);
  await seedDb(config);
  const mswData = mapDemoConfigToMswData(config);
  const supplier = toDemoSupplier(mswData.supplier);

  return {
    supplier,
    handlers: buildDemoHandlers(mswData, {
      getSupplierMarkets,
      getOffers,
      getPlayableInvoices,
      getPlayableInvoiceStats,
      getPlayableInvoicesCsv,
      getClearingInvoices,
      getClearingInvoiceStats,
      getPricingSuggestions: () => mswData.pricingSuggestions,
      getSupplierMarketClears,
      getRecentClears,
      getSupplierMarketClearsStats,
      submitOffer,
      editOffer,
      submitStaticOffer,
      editStaticOffer,
      submitBenchmarkOffer,
      editBenchmarkOffer,
      submitExpressAcceptOffer,
      toggleOffer,
      manageExclusions,
    }),
  };
}
